import React from 'react';
import CustomChartRenderer from './CustomChartRenderer';
import {
  LineChart,
  BarChart,
  AreaChart,
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Metric } from '../types';

interface ChartRendererProps {
  metric: Metric;
}

const ChartRenderer: React.FC<ChartRendererProps> = ({ metric }) => {
  // If it's a custom chart, use the custom renderer
  if (metric.chart_type === 'custom') {
    return <CustomChartRenderer metric={metric} />;
  }

  // Convert metric data to recharts compatible format
  const chartData = metric.data
    .filter(item => {
      // Filter out invalid entries where both x and y values are empty
      return !(item.x_value === '' && (item.y_value === '' || item.y_value === null || item.y_value === undefined));
    })
    .map(item => {
      // Process X value - keep as string for categorical data or convert if numeric
      const x = typeof item.x_value === 'number' ? item.x_value : String(item.x_value);
      
      // Process Y value - ensure it's numeric for proper line chart rendering
      let y: number;
      if (typeof item.y_value === 'number') {
        y = item.y_value;
      } else {
        const parsedValue = parseFloat(item.y_value.toString());
        y = isNaN(parsedValue) ? 0 : parsedValue;
      }
      
      return { x, y };
    });

  const renderChart = () => {
    switch (metric.chart_type) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
              <XAxis 
                dataKey="x" 
                stroke="#94a3b8" 
                label={{ value: metric.x_label, position: 'insideBottomRight', offset: -5 }} 
              />
              <YAxis 
                stroke="#94a3b8" 
                label={{ value: metric.y_label, angle: -90, position: 'insideLeft', offset: -15 }} 
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#e2e8f0' }} 
                itemStyle={{ color: '#e2e8f0' }}
              />
              <Legend formatter={() => ''} />
              <Line 
                type="monotone" 
                dataKey="y" 
                stroke="#3a86ff" 
                activeDot={{ r: 8 }} 
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
              <XAxis 
                dataKey="x" 
                stroke="#94a3b8" 
                label={{ value: metric.x_label, position: 'insideBottomRight', offset: -5 }} 
              />
              <YAxis 
                stroke="#94a3b8" 
                label={{ value: metric.y_label, angle: -90, position: 'insideLeft', offset: -15 }} 
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#e2e8f0' }} 
                itemStyle={{ color: '#e2e8f0' }}
              />
              <Legend formatter={() => ''} />
              <Bar dataKey="y" fill="#3a86ff" />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
              <XAxis 
                dataKey="x" 
                stroke="#94a3b8" 
                label={{ value: metric.x_label, position: 'insideBottomRight', offset: -5 }} 
              />
              <YAxis 
                stroke="#94a3b8" 
                label={{ value: metric.y_label, angle: -90, position: 'insideLeft', offset: -15 }} 
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', color: '#e2e8f0' }} 
                itemStyle={{ color: '#e2e8f0' }}
              />
              <Legend formatter={() => ''} />
              <Area 
                type="monotone" 
                dataKey="y" 
                stackId="1" 
                stroke="#3a86ff" 
                fill="#3a86ff" 
                fillOpacity={0.6} 
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      default:
        return (
          <div className="text-center py-10 text-medium-gray">
            Unsupported chart type: {metric.chart_type}
          </div>
        );
    }
  };

  return (
    <div className="bg-dark-navy p-4 rounded-lg">
      {renderChart()}
    </div>
  );
};

export default ChartRenderer;